import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { authorAuthService } from '../../features/author/services/authorAuth';
import TetfundLogo from '../../assets/images/TetfundLogo.svg';

const VerifyAccount: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState('');
    const [verifyingLink, setVerifyingLink] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const getQueryParam = (param: string) => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get(param);
    };

    //fghvjb

    const verifyCalled = React.useRef(false);

    useEffect(() => {
        if (location.state?.email) {
            setEmail(location.state.email);
        }

        const urlCode = getQueryParam('code');

        if (urlCode && verificationStatus === 'idle' && !verifyCalled.current) {
            verifyCalled.current = true;
            setVerifyingLink(true);
            handleAutoVerify(urlCode);
        }
    }, [location.search]);

    const handleAutoVerify = async (verificationCode: string) => {
        try {
            const response = await authorAuthService.verifyAccount(verificationCode);

            if (response.isSuccess) {
                setVerificationStatus('success');
                Swal.fire({
                    icon: 'success',
                    title: 'Verified!',
                    text: response.message || 'Your account has been successfully verified.',
                    confirmButtonColor: '#00A63E',
                    timer: 2000,
                    showConfirmButton: false
                }).then(() => {
                    navigate('/login');
                });
            } else {
                setVerificationStatus('error');
                setVerifyingLink(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Verification Failed',
                    text: response.message,
                    confirmButtonColor: '#00A63E'
                });
            }
        } catch (error: any) {
            setVerificationStatus('error');
            setVerifyingLink(false);
            Swal.fire({
                icon: 'error',
                title: 'Verification Failed',
                text: error?.response?.data?.message || 'Network error occurred.',
                confirmButtonColor: '#00A63E'
            });
        }
    };

    return (
        <div className="flex min-h-screen bg-white">
            <div className="hidden lg:flex lg:w-1/2 bg-[#00A63E] flex-col justify-between p-16 text-white sticky top-0 h-screen">
                <div>
                    <div className="flex items-center gap-3">
                        <img src={TetfundLogo} alt="Logo" className="bg-white p-1.5 rounded h-10" />
                        <span className="text-xl font-medium tracking-wide">TAG Portal</span>
                    </div>
                </div>
                <div className="max-w-md">
                    <h1 className="text-5xl font-normal leading-[1.15] mb-6">Verify Your Account</h1>
                    <p className="text-lg opacity-80 leading-relaxed font-light">
                        Please wait while we validate your credentials and secure your access to the portal.
                    </p>
                </div>
                <p className="text-sm opacity-70">© {new Date().getFullYear()} TETFund TAG. All rights reserved.</p>
            </div>

            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-md text-center">

                    {verifyingLink ? (
                        <div className="flex flex-col items-center animate-in fade-in duration-700">
                            <div className="relative mb-6">
                                <div className="w-20 h-20 border-4 border-gray-100 rounded-full"></div>
                                <div className="w-20 h-20 border-4 border-[#00A63E] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Account...</h2>
                            <p className="text-gray-500">Please wait while we validate your email link.</p>
                        </div>
                    ) : (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="w-20 h-20 bg-[#F3F4F6] rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-10 h-10 text-[#00A63E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">Check your email</h2>
                            <p className="text-gray-500 mb-8 leading-relaxed">
                                We sent a verification link to <span className="font-semibold text-gray-900">{email || 'your email'}</span>.
                                Click the link in the message to activate your account.
                            </p>

                            <div className="space-y-6">
                                {/* <button
                                    type="button"
                                    className="w-full py-3.5 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all"
                                    onClick={() => { }}
                                >
                                    Didn't receive the email? Resend
                                </button> */}

                                <Link to="/login" className="inline-flex items-center text-sm text-gray-500 font-medium hover:text-[#155DFC]">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                    </svg>
                                    Back to Login
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyAccount;